import React, { useCallback, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { generateFullName } from 'utils/generateFullNameUtils';
import { Tooltip } from 'primereact/tooltip';
import { ReactComponent as KanbanBoardIcon } from 'assets/svg/kanban_board.svg';

import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { dateFilterOptions, taskPrioritySortList } from 'constants/dropdownOptions';
import { sortByKey } from 'utils/utils';
import { LoaderComponent } from 'shared/LoaderComponent';
import { useFilters } from '../../context/FilterContext';
import { useBoardData } from '../../context/BoardContext';
import { handleFilterResetUserContext } from 'common/pagination';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useBoardActions, useBoardSection } from 'modules/TasksV2/context';
import { queryClient } from 'config/queryClient';
import { queryKeys } from 'modules/TasksV2/config/queryClient';
import { Button } from 'primereact/button';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';

function Filter({ currentView, setCurrentView }) {
  const userContext = useUserDetailsContext();
  const { isGlobal } = useBoardSection();
  const { resetBoard } = useBoardActions();
  // Track if we're currently toggling views to prevent duplicate API calls
  const isTogglingRef = useRef(false);

  // Get filter state and actions from FilterContext
  const {
    searchInput, // Use searchInput for the input field (immediate updates)
    practiceArea,
    assignedTo,
    priority,
    setPriority,
    sortType,
    dateFilter,
    setSearchInput, // Use setSearchInput to update search
    setPracticeArea,
    setAssignedTo,
    setSortType,
    setDateFilter,
    clearFilters,
  } = useFilters();

  // Get board data (practiceAreaList, userList, etc.)
  const { practiceAreaList, isPracticeAreaLoading, userList, isLoadingUsers, clientList, isLoadingClients } = useBoardData();

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const filterObj = { ...(userContext?.userDetails?.user_preferences?.filters?.tasks || {}), [name]: value };
      switch (name) {
        case 'keyword':
          setSearchInput(value); // Update search input (debouncing handled in FilterContext)
          break;
        case 'assigned_to':
          setAssignedTo(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'practice_area':
          setPracticeArea(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'sort_by':
          setSortType(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'priority':
          setPriority(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'date':
          setDateFilter(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        default:
          break;
      }
    },
    [setSearchInput, setAssignedTo, setPracticeArea, setSortType, setDateFilter, userContext?.userDetails?.user_preferences?.filters?.tasks]
  );

  const resetFilters = useCallback(() => {
    clearFilters();
    const resetFilterObj = {
      assigned_to: null,
      practice_area: null,
      priority: null,
      date: null,
    };
    queryClient.invalidateQueries({ queryKey: [...queryKeys.tasks.all] });
    isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, resetFilterObj, true);
  }, [clearFilters]);

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {generateFullName(option)}
      </>
    );
  };
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-2 w-100">
      <div className="d-flex flex-wrap w-100 align-items-center justify-content-between gap-2">
        {isGlobal && (
          <div className="autowidth">
            <span className="p-input-icon-left input-shadow autowidth">
              <i className="icon-search F-size16 cursor-pointer" />
              <TextSnippetWrapper
                type="text"
                placeholder="Search By Task Name"
                name="keyword"
                value={searchInput}
                onChange={handleFilterChange}
                className="input-search"
              />
            </span>
          </div>
        )}
        <div className="d-block d-lg-none w-100">
          <Button
            className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            aria-controls="task-filter-section"
            label="Filters"
          >
            <span
              style={{
                fontSize: 18,
                transition: 'transform 0.2s',
                display: 'inline-block',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <i className="fa fa-solid fa-angle-down"></i>
            </span>
          </Button>
        </div>
        <div
          className={`flex-wrap d-flex align-items-center filter-wraper gap-2 filter-toggle-section filter-section${
            showFilters ? ' show' : ''
          }`}
          id="task-filter-section"
          aria-hidden={!showFilters}
        >
          <span className="text-bold black-600  filter-label py-0">Filters: </span>
          <div className="d-flex flex-column task-filter py-0">
            {isGlobal ? (
              <MultiSelect
                name="assigned_to"
                value={assignedTo}
                onChange={handleFilterChange}
                options={sortByKey(userList, 'first_name') ?? []}
                optionLabel={(e) => generateFullName(e)}
                isSearchable
                placeholder="Select Assignee"
                className="w-100 input-shadow input-width input-height"
                optionValue="firm_user_id"
                filter
                filterPlaceholder="Search"
                resetFilterOnHide
                itemTemplate={assignToItemTemplate}
                panelFooterTemplate={isLoadingUsers ? LoaderComponent : null}
                dropdownIcon={isLoadingUsers ? 'pi pi-spinner pi-spin' : undefined}
              />
            ) : (
              <MultiSelect
                value={assignedTo}
                options={sortByKey(clientList, 'first_name') ?? []}
                onChange={handleFilterChange}
                filterBy="first_name,last_name"
                placeholder="Select Users"
                filter
                className="searchable-dropdown input-shadow"
                itemTemplate={assignToItemTemplate}
                maxSelectedLabels={1}
                showSelectAll={false}
                optionValue={(v) => v?.firm_user_id || v?.contact_id}
                optionLabel={(e) => generateFullName(e)}
                name="assigned_to"
                showClear
                panelFooterTemplate={isLoadingClients ? LoaderComponent : null}
                dropdownIcon={isLoadingClients ? 'pi pi-spinner pi-spin' : undefined}
                resetFilterOnHide
              />
            )}
          </div>
          {isGlobal && (
            <div className="d-flex flex-column task-filter py-0">
              <Dropdown
                name="practice_area"
                value={practiceArea}
                placeholder="Select Practice Area"
                options={practiceAreaList?.filter((item) => item.status === 'ACTIVE') ?? []}
                filter
                filterPlaceholder="Search"
                optionLabel="practice_area_name"
                optionValue="practice_area_id"
                className="w-100 input-shadow autowidth input-height"
                onChange={handleFilterChange}
                resetFilterOnHide
                panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
              />
            </div>
          )}
          <PriorityDropdown value={priority} onChange={handleFilterChange} name="priority" showClear />
          <div className="d-flex flex-column task-filter py-0">
            <Dropdown
              value={dateFilter}
              options={dateFilterOptions}
              onChange={handleFilterChange}
              placeholder="By Due Date"
              name="date"
              showClear
            />
          </div>
          <div className="d-flex flex-column task-filter py-0">
            <Dropdown
              name="sort_by"
              value={sortType}
              placeholder="Sort by"
              options={taskPrioritySortList}
              optionLabel="label"
              optionValue="value"
              onChange={handleFilterChange}
              resetFilterOnHide
            />
          </div>
          <div className="d-flex pointer align-items-center p-2">
            <i className="fas fa-sync-alt" onClick={resetFilters} />
            <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-end gap-2 change-view">
          <Button
            icon={<KanbanBoardIcon width={18} height={18} aria-hidden="true" focusable="false" />}
            className={`kanban-view-icon ${currentView === 'kanban' ? 'highlight' : ''}`}
            onClick={() => {
              // Prevent duplicate calls during toggle
              if (isTogglingRef.current) return;
              isTogglingRef.current = true;

              setCurrentView('kanban');
              if (searchInput || practiceArea || assignedTo?.length > 0 || sortType || dateFilter || priority) {
                resetFilters();
              }
              // Invalidate queries - they will refetch when components mount
              // Using invalidateQueries prevents immediate refetch, components will refetch when they mount
              queryClient.invalidateQueries({
                queryKey: ['tasks', 'initial-load'],
              });

              // Reset toggle flag after components have mounted and queries have started
              setTimeout(() => {
                isTogglingRef.current = false;
              }, 300);
            }}
          >
            <Tooltip content="Kanban View" position="left" target=".kanban-view-icon" showDelay={500} />
          </Button>
          <Button
            icon={<i className="icon-List-view" style={{ fontSize: 18 }}></i>}
            className={`agenda-view-icon ${currentView === 'agenda' ? 'highlight' : ''}`}
            onClick={() => {
              // Prevent duplicate calls during toggle
              if (isTogglingRef.current) return;
              isTogglingRef.current = true;

              setCurrentView('agenda');
              if (searchInput || practiceArea || assignedTo?.length > 0 || sortType || dateFilter || priority) {
                resetFilters();
              }
              // Invalidate queries to mark them as stale
              // Components will refetch when they mount (via refetchOnMount logic)
              queryClient.invalidateQueries({
                queryKey: ['agendaTasks', 'column'],
              });

              // After a short delay, refetch active queries to ensure API is called
              // This handles the case where components mount after invalidation
              setTimeout(() => {
                queryClient.refetchQueries({
                  queryKey: ['agendaTasks', 'column'],
                  type: 'active', // Only refetch queries that are currently active (mounted)
                });
              }, 50);

              // Reset toggle flag after components have mounted and queries have started
              setTimeout(() => {
                isTogglingRef.current = false;
              }, 300);
            }}
          >
            <Tooltip content="Agenda View" position="left" target=".agenda-view-icon" showDelay={500} />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Memoize to prevent unnecessary rerenders
export default React.memo(Filter);
